import { useState, useRef, useCallback, useMemo, useEffect } from 'react';

// Safer regex to avoid ReDoS; validates common email format
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MIN_PASSWORD_LENGTH = 8;
export const MAX_EMAILS = 10;

const isValidEmail = (value) => EMAIL_REGEX.test((value || '').trim());
const isPasswordLongEnough = (value) => (value || '').trim().length >= MIN_PASSWORD_LENGTH;

export const useExportAllWithEmailHook = (props) => {
    const { open = false, onClose, onExport } = props;

    const [emails, setEmails] = useState(['']);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const modalRef = useRef(null);
    const closingFromUserRef = useRef(false);
    const wasOpenRef = useRef(false);

    const handleClose = useCallback(() => {
        closingFromUserRef.current = true;
        if (modalRef.current?.toggleModal) {
            modalRef.current.toggleModal();
        }
        setEmails(['']);
        setPassword('');
        setShowPassword(false);
        onClose?.();
    }, [onClose]);

    /**
     * Sync with frontend-commons Modal: call toggleModal when open changes.
     * When open becomes true, open the modal; when open becomes false (from parent, e.g. 50K error), close it.
     * Only close when transitioning from open to closed (not on initial mount with open=false).
     */
    useEffect(() => {
        if (!modalRef.current?.toggleModal) return;
        if (open) {
            closingFromUserRef.current = false;
            wasOpenRef.current = true;
            modalRef.current.toggleModal();
        } else if (wasOpenRef.current && !closingFromUserRef.current) {
            wasOpenRef.current = false;
            handleClose();
        } else {
            wasOpenRef.current = false;
            closingFromUserRef.current = false;
        }
    }, [open, handleClose]);

    const handleAddEmail = useCallback(() => {
        setEmails((prev) => (prev.length >= MAX_EMAILS ? prev : [...prev, '']));
    }, []);

    const handleEmailChange = useCallback((index, value) => {
        setEmails((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }, []);

    const handleRemoveEmail = useCallback((index) => {
        setEmails((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    }, []);

    const hasAtLeastOneEmail = useMemo(
        () => emails.some((e) => isValidEmail(e)),
        [emails],
    );
    const isPasswordValid = useMemo(
        () => (password || '').trim().length >= MIN_PASSWORD_LENGTH,
        [password],
    );
    const isFormValid = hasAtLeastOneEmail && isPasswordValid;

    const passwordError = useMemo(() => {
        const trimmed = (password || '').trim();
        if (trimmed.length === 0) return null;
        return isPasswordLongEnough(password) ? null : `Must be at least ${MIN_PASSWORD_LENGTH} characters long`;
    }, [password]);

    const handleContinue = useCallback(() => {
        if (!isFormValid) return;
        const validEmails = emails.filter((e) => isValidEmail(e));
        onExport?.({ emails: validEmails, password: password.trim() });
    }, [isFormValid, emails, password, onExport]);

    const handlePasswordChange = useCallback((e) => {
        setPassword(e.target.value);
    }, []);

    const handleToggleShowPassword = useCallback(() => {
        setShowPassword((p) => !p);
    }, []);

    return {
        modalRef,
        emails,
        password,
        showPassword,
        isFormValid,
        passwordError,
        handleAddEmail,
        handleEmailChange,
        handleRemoveEmail,
        handleClose,
        handleContinue,
        handlePasswordChange,
        handleToggleShowPassword,
        isValidEmail,
    };
};
